import React from "react";
import { Box, Typography, ListItem, ListItemIcon } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as CheckMarkCircle } from "../../../icons/CheckMarkCircle.svg";
import { IphoneIconPreview } from "../IphoneIconPreview";
import { useCurrentBrand } from "../../../hooks/useCurrentWorkspace";
import {
  drawImageWithAspect,
  generateWatermakedCanvasImage,
  getImageBitmap,
} from "../../../utils/canvas";
import demoProgressPhoto from "../../../assets/demo-progress-photo.png";
import watermark from "../../app/logo-full-mono.png";
import { SharablePreviewCanvas } from "./SharablePreviewCanvas";
import demoWorkoutSharing from "../../../assets/workout-sharing-demo.png";
import { IphoneSplashPreview } from "../IphoneSplashPreview";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2.5),
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },
  statusBox: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    marginBottom: 4,
  },
  status: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.common.black,
  },
  features: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    marginBottom: theme.spacing(4.5),
    marginTop: theme.spacing(3),
  },
  feature: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing(3.5),
    },

    [theme.breakpoints.up("md")]: {
      "&:not(:last-child)": {
        marginBottom: theme.spacing(3.5),
      },
    },
  },
  check: {
    marginRight: theme.spacing(1.25),

    "& svg": {
      width: 30,
      height: 30,
      color: theme.palette.primary.main,
    },
  },

  uploadPreview: {
    width: "100%",
    display: "flex",
    justifyContent: "start",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "start",
    marginBottom: theme.spacing(4),

    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
    },
  },

  sharablePreview: {
    paddingTop: theme.spacing(-2),
    maxWidth: theme.spacing(42),
  },
}));

interface PendingCoachReviewContentProps {}

export function PendingCoachReviewContent() {
  const s = useStyles();
  const {
    brandName,
    appName,
    brandedAppLogo,
    brandedAppBanner,
    brandedAppSplashScreen,
  } = useCurrentBrand();

  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    const generateCanvas = async () => {
      if (canvasRef.current) {
        //TODO: replace with uploaded watermark
        generateWatermakedCanvasImage(
          drawImageWithAspect(await getImageBitmap(demoProgressPhoto), 1),
          canvasRef,
          brandedAppBanner || watermark,
        );
      }
    };
    generateCanvas();
  }, [brandedAppBanner]);

  return (
    <Box className={s.header}>
      <Box className={s.titleBox}>
        <Typography className={s.title}>{brandName} Branded App</Typography>
        <Box className={s.statusBox}>
          <Typography className={s.status}>pending your review</Typography>
        </Box>
      </Box>
      <Typography className={s.text}>
        Your branded app is ready for your final review. Please check the
        following details:
      </Typography>
      <Box className={s.features}>
        <ListItem className={s.feature}>
          <ListItemIcon className={s.check} children={<CheckMarkCircle />} />
          <Typography className={s.text}>App name: {appName}</Typography>
        </ListItem>
        <ListItem className={s.feature}>
          <ListItemIcon className={s.check} children={<CheckMarkCircle />} />
          <Typography className={s.text}>App icon and splash screen</Typography>
        </ListItem>
        <Box className={s.uploadPreview}>
          <Box className={s.sharablePreview}>
            <IphoneIconPreview
              iconUrl={brandedAppLogo}
              appName={appName}
              previewTitle="Branded App Preview"
            />
          </Box>
          <IphoneSplashPreview
            splashUrl={brandedAppSplashScreen}
            paddingTop={false}
          />
        </Box>
        <ListItem className={s.feature}>
          <ListItemIcon className={s.check} children={<CheckMarkCircle />} />
          <Typography className={s.text}>
            Internal app branding elements
          </Typography>
        </ListItem>
        <Box className={s.uploadPreview}>
          <SharablePreviewCanvas
            brandedAppBanner={brandedAppBanner}
            backgroundUrl={demoWorkoutSharing}
            title="Workout Sharing Preview"
          />
          <SharablePreviewCanvas brandedAppBanner={brandedAppBanner} />
        </Box>
        <ListItem className={s.feature}>
          <ListItemIcon className={s.check} children={<CheckMarkCircle />} />
          <Typography className={s.text}>
            Contact us to receive a preview of your branded app
          </Typography>
        </ListItem>
      </Box>
      <Typography className={s.text} sx={{ marginTop: 2 }}>
        Once you approve, we'll proceed with publishing your app to the stores.
      </Typography>
    </Box>
  );
}
